import {
  ArchiveRestore,
  Boxes,
  Building2,
  ClipboardList,
  Gauge,
  History,
  Users,
  LogOut,
  Menu,
  PackageCheck,
  Search,
  Settings,
  Truck,
  UsersRound,
  X
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

const navigation = [
  { to: "/dashboard", label: "لوحة المتابعة", icon: Gauge, permission: "household.view" },
  { to: "/units", label: "الوحدات الجغرافية", icon: Building2, permission: "area.view" },
  { to: "/households", label: "الأسر", icon: Users, permission: "household.view" },
  { to: "/imports", label: "الاستيراد", icon: ArchiveRestore, permission: "household.import" },
  { to: "/agents", label: "الوكلاء", icon: Truck, permission: "agent.view" },
  { to: "/cycles", label: "الدورات", icon: Boxes, permission: "distribution.view" },
  { to: "/batches", label: "الدفعات", icon: ClipboardList, permission: "distribution.view" },
  { to: "/delivery", label: "التسليم", icon: PackageCheck, permission: "distribution.deliver" },
  { to: "/reports", label: "التقارير", icon: Building2, permission: "report.view" },
  { to: "/audit", label: "سجل التدقيق", icon: History, permission: "audit.view" },
  { to: "/admin", label: "الإدارة", icon: UsersRound, permission: "user.view" },
  { to: "/settings", label: "الإعدادات", icon: Settings, permission: "settings.manage" }
] as const;

export function AppShell() {
  const { session, can, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => setMenuOpen(false), [location.pathname]);

  async function handleLogout() {
    await logout();
    void navigate("/login", { replace: true });
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        انتقل إلى المحتوى
      </a>
      <header className="topbar">
        <button
          className="icon-button mobile-only"
          type="button"
          onClick={() => setMenuOpen((value) => !value)}
          aria-expanded={menuOpen}
          aria-controls="main-navigation"
          aria-label={menuOpen ? "إغلاق القائمة" : "فتح القائمة"}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
        <NavLink className="brand" to="/" aria-label="تموين - الصفحة الرئيسية">
          <span className="brand-mark" aria-hidden="true">
            ت
          </span>
          <span>
            <strong>تموين</strong>
            <small>إدارة عادلة وواضحة للتوزيع</small>
          </span>
        </NavLink>
        <div className="topbar-actions">
          <button
            className="icon-button"
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="فتح البحث"
          >
            <Search aria-hidden="true" />
          </button>
          <div className="user-chip">
            <span>{session?.displayName}</span>
            <small>موظف</small>
          </div>
          <button className="icon-button" type="button" onClick={handleLogout} aria-label="تسجيل الخروج">
            <LogOut aria-hidden="true" />
          </button>
        </div>
      </header>

      <aside id="main-navigation" className={`sidebar ${menuOpen ? "sidebar--open" : ""}`}>
        <nav aria-label="التنقل الرئيسي">
          {navigation
            .filter((item) => can(item.permission))
            .map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
              >
                <Icon aria-hidden="true" />
                <span>{label}</span>
              </NavLink>
            ))}
        </nav>
        <p className="sidebar-foot">البيانات محمية حسب نطاق صلاحيتك</p>
      </aside>

      <main id="main-content" className="main-content" tabIndex={-1}>
        <Outlet />
      </main>

      {searchOpen ? <GlobalSearch onClose={() => setSearchOpen(false)} /> : null}
      {menuOpen ? (
        <button
          className="backdrop"
          type="button"
          aria-label="إغلاق القائمة"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
    </div>
  );
}

function GlobalSearch({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  return (
    <div
      className="dialog-layer"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="search-dialog" role="dialog" aria-modal="true" aria-labelledby="search-title">
        <div className="dialog-head">
          <h2 id="search-title">بحث سريع</h2>
          <button className="icon-button" type="button" onClick={onClose} aria-label="إغلاق البحث">
            <X aria-hidden="true" />
          </button>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (query.trim().length >= 2) {
              void navigate(`/households?search=${encodeURIComponent(query.trim())}`);
              onClose();
            }
          }}
        >
          <label htmlFor="global-search">اسم رب الأسرة أو رقم الأسرة</label>
          <div className="search-row">
            <input
              id="global-search"
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              minLength={2}
              required
            />
            <button className="button" type="submit">
              بحث
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
